import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Activity, Calendar, Users, Clock, Filter, Plus, Menu, X, 
  ChevronLeft, ChevronRight, Trash2, Edit2, Palmtree 
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, subDays, subWeeks, subMonths, eachDayOfInterval, parseISO } from "date-fns";

// Helper for authenticated API calls
const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

const WORK_TYPES = [
  { value: "assistant_nurse", label: "Assistent/Õde", color: "#64748B" },
  { value: "endocrine_nurse", label: "Endokrinoloogiaõde", color: "#8B5CF6" },
  { value: "accountant", label: "Raamatupidaja", color: "#0EA5E9" },
];

// Distinct colors for nurses
const NURSE_COLORS = [
  "#E11D48", // rose
  "#7C3AED", // violet
  "#0891B2", // cyan
  "#059669", // emerald
  "#D97706", // amber
  "#DC2626", // red
  "#4F46E5", // indigo
  "#0D9488", // teal
  "#9333EA", // purple
  "#EA580C", // orange
  "#2563EB", // blue
  "#65A30D", // lime
  "#DB2777", // pink
  "#0284C7", // sky
  "#CA8A04", // yellow
];

const TIME_PERIODS = [
  { value: "this_week", label: "See nädal" },
  { value: "last_week", label: "Eelmine nädal" },
  { value: "this_month", label: "See kuu" },
  { value: "last_month", label: "Eelmine kuu" },
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [nurses, setNurses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Filters
  const [timePeriod, setTimePeriod] = useState("this_week");
  const [selectedNurse, setSelectedNurse] = useState("all");
  const [selectedWorkType, setSelectedWorkType] = useState("all");
  
  // Schedule Dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    nurse_id: "",
    date: format(new Date(), "yyyy-MM-dd"),
    start_time: "8",
    end_time: "16",
    work_type: "assistant_nurse",
    notes: "",
  });

  // Vacations
  const VACATION_NAMES = ["Liina", "Riina", "Kersti", "Kristel", "Verner"];
  const [vacations, setVacations] = useState(
    VACATION_NAMES.map(name => ({ name, start_date: "", end_date: "" }))
  );
  const [vacationSavingName, setVacationSavingName] = useState(null);

  // Generate time options in 30-minute intervals
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    timeOptions.push({ value: h.toString(), label: `${h.toString().padStart(2, "0")}:00` });
    timeOptions.push({ value: (h + 0.5).toString(), label: `${h.toString().padStart(2, "0")}:30` });
  }
  timeOptions.push({ value: "24", label: "24:00" });

  // Calculate date range based on time period
  const dateRange = useMemo(() => {
    const today = new Date();
    let start, end;
    
    switch (timePeriod) {
      case "this_week":
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case "last_week":
        start = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        end = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        break;
      case "this_month":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "last_month":
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        break;
      default:
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
    }
    
    return { start, end, days: eachDayOfInterval({ start, end }) };
  }, [timePeriod]);

  // Fetch data
  useEffect(() => {
    fetchNurses();
    fetchSchedules();
    fetchVacations();
  }, [dateRange]);

  const fetchNurses = async () => {
    try {
      const response = await authFetch(`${API}/nurses`);
      if (response.ok) {
        const data = await response.json();
        setNurses(data);
      }
    } catch (error) {
      console.error("Failed to fetch nurses:", error);
      toast.error("Failed to load nurses");
    }
  };

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const startDate = format(dateRange.start, "yyyy-MM-dd");
      const endDate = format(dateRange.end, "yyyy-MM-dd");
      const response = await authFetch(
        `${API}/schedules?start_date=${startDate}&end_date=${endDate}`
      );
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
      toast.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  const fetchVacations = async () => {
    try {
      const response = await authFetch(`${API}/vacations`);
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setVacations(VACATION_NAMES.map(name => {
            const found = data.find(v => v.name === name);
            return found || { name, start_date: "", end_date: "" };
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch vacations:", error);
    }
  };

  const saveVacation = async (index) => {
    const v = vacations[index];
    setVacationSavingName(v.name);
    try {
      const response = await authFetch(`${API}/vacations`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(v),
      });
      if (response.ok) {
        toast.success(`${v.name} puhkus salvestatud`);
      } else {
        toast.error("Salvestamine ebaõnnestus");
      }
    } catch (error) {
      toast.error("Salvestamine ebaõnnestus");
    } finally {
      setVacationSavingName(null);
    }
  };

  const updateVacation = (index, field, value) => {
    setVacations(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  };

  // Filter schedules
  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      if (selectedNurse !== "all" && schedule.nurse_id !== selectedNurse) return false;
      if (selectedWorkType !== "all" && schedule.work_type !== selectedWorkType) return false;
      return true;
    });
  }, [schedules, selectedNurse, selectedWorkType]);

  // Handle schedule form
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingSchedule 
        ? `${API}/schedules/${editingSchedule.schedule_id}`
        : `${API}/schedules`;
      
      const response = await authFetch(url, {
        method: editingSchedule ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...scheduleForm,
          start_time: scheduleForm.start_time,
          end_time: scheduleForm.end_time,
        }),
      });

      if (response.ok) {
        toast.success(editingSchedule ? "Schedule updated" : "Schedule created");
        setScheduleDialogOpen(false);
        setEditingSchedule(null);
        resetScheduleForm();
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save schedule");
      }
    } catch (error) {
      console.error("Failed to save schedule:", error);
      toast.error("Failed to save schedule");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;
    
    try {
      const response = await authFetch(`${API}/schedules/${scheduleId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Schedule deleted");
        fetchSchedules();
      } else {
        toast.error("Failed to delete schedule");
      }
    } catch (error) {
      console.error("Failed to delete schedule:", error);
      toast.error("Failed to delete schedule");
    }
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      nurse_id: "",
      date: format(new Date(), "yyyy-MM-dd"),
      start_time: "8",
      end_time: "16",
      work_type: "assistant_nurse",
      notes: "",
    });
  };

  const openEditDialog = (schedule) => {
    setEditingSchedule(schedule);
    const startTime = schedule.start_time !== undefined ? schedule.start_time : schedule.start_hour;
    const endTime = schedule.end_time !== undefined ? schedule.end_time : schedule.end_hour;
    setScheduleForm({
      nurse_id: schedule.nurse_id,
      date: schedule.date,
      start_time: String(parseTime(startTime)),
      end_time: String(parseTime(endTime)),
      work_type: schedule.work_type,
      notes: schedule.notes || "",
    });
    setScheduleDialogOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Get nurse name by ID
  const getNurseName = (nurseId) => {
    const nurse = nurses.find((n) => n.nurse_id === nurseId);
    return nurse ? nurse.name : "Unknown";
  };

  // Get nurse color by ID (consistent color per nurse)
  const getNurseColor = (nurseId) => {
    const nurseIndex = nurses.findIndex((n) => n.nurse_id === nurseId);
    if (nurseIndex === -1) return NURSE_COLORS[0];
    return NURSE_COLORS[nurseIndex % NURSE_COLORS.length];
  };

  // Get work type info
  const getWorkType = (value) => {
    return WORK_TYPES.find((w) => w.value === value) || WORK_TYPES[0];
  };

  // Parse time value (string "HH:MM" or number) to decimal hours
  const parseTime = (time) => {
    if (typeof time === "string" && time.includes(":")) {
      const [h, m] = time.split(":").map(Number);
      return h + (m || 0) / 60;
    }
    return parseFloat(time) || 0;
  };

  // Helper to format time for display
  const formatTime = (time) => {
    const t = parseTime(time);
    const hours = Math.floor(t);
    const minutes = Math.round((t % 1) * 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  };

  // Calculate total hours for stats
  const totalHours = filteredSchedules.reduce((sum, s) => {
    const start = parseTime(s.start_time ?? s.start_hour);
    const end = parseTime(s.end_time ?? s.end_hour);
    return sum + (end - start);
  }, 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="dashboard-page">
      <AppSidebar open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setMobileMenuOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Töögraafik</h1>
                <p className="text-sm text-slate-500">
                  {format(dateRange.start, "MMM d")} - {format(dateRange.end, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            
            <Dialog open={scheduleDialogOpen} onOpenChange={(open) => {
              setScheduleDialogOpen(open);
              if (!open) {
                setEditingSchedule(null);
                resetScheduleForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-schedule-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Lisa graafik</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingSchedule ? "Muuda graafikut" : "Lisa graafik"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleScheduleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nurse</Label>
                    <Select
                      value={scheduleForm.nurse_id}
                      onValueChange={(value) => setScheduleForm({ ...scheduleForm, nurse_id: value })}
                    >
                      <SelectTrigger data-testid="schedule-nurse-select">
                        <SelectValue placeholder="Select nurse" />
                      </SelectTrigger>
                      <SelectContent>
                        {nurses.map((nurse) => (
                          <SelectItem key={nurse.nurse_id} value={nurse.nurse_id}>
                            {nurse.name} ({nurse.employee_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                      required
                      data-testid="schedule-date-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Algusaeg</Label>
                      <Select
                        value={scheduleForm.start_time}
                        onValueChange={(value) => setScheduleForm({ ...scheduleForm, start_time: value })}
                      >
                        <SelectTrigger data-testid="schedule-start-time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.slice(0, -1).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lõppaeg</Label>
                      <Select
                        value={scheduleForm.end_time}
                        onValueChange={(value) => setScheduleForm({ ...scheduleForm, end_time: value })}
                      >
                        <SelectTrigger data-testid="schedule-end-time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.slice(1).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Work Type</Label>
                    <Select
                      value={scheduleForm.work_type}
                      onValueChange={(value) => setScheduleForm({ ...scheduleForm, work_type: value })}
                    >
                      <SelectTrigger data-testid="schedule-work-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WORK_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: type.color }}
                              />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Input
                      value={scheduleForm.notes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                      placeholder="Additional notes..."
                      data-testid="schedule-notes-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setScheduleDialogOpen(false);
                        setEditingSchedule(null);
                        resetScheduleForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-slate-900 hover:bg-slate-800"
                      data-testid="schedule-submit-btn"
                    >
                      {editingSchedule ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-6">
          {/* Stats + Vacations side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
            {/* Stats Cards - 2x2 grid */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{filteredSchedules.length}</p>
                      <p className="text-xs text-slate-500">Vahetusi</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{totalHours}</p>
                      <p className="text-xs text-slate-500">Tunde kokku</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{nurses.length}</p>
                      <p className="text-xs text-slate-500">Õdesid kokku</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {totalHours > 0 ? (totalHours / Math.max(filteredSchedules.length, 1)).toFixed(1) : 0}
                      </p>
                      <p className="text-xs text-slate-500">Kesk. tundi/vahetus</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vacation Planning - next to stats */}
            <Card className="border-slate-200" data-testid="vacation-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Palmtree className="w-5 h-5 text-emerald-600" /> Planeeritud puhkused
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="space-y-3">
                  {vacations.map((v, i) => (
                    <div key={v.name} data-testid={`vacation-row-${v.name}`}>
                      <span className="text-sm font-medium text-slate-700 block mb-1 sm:hidden">{v.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="w-16 text-sm font-medium text-slate-700 flex-shrink-0 hidden sm:block">{v.name}</span>
                        <Input
                          type="date"
                          value={v.start_date}
                          onChange={(e) => updateVacation(i, "start_date", e.target.value)}
                          className="flex-1 min-w-0 text-sm"
                          data-testid={`vacation-start-${v.name}`}
                        />
                        <span className="text-slate-400 flex-shrink-0">—</span>
                        <Input
                          type="date"
                          value={v.end_date}
                          onChange={(e) => updateVacation(i, "end_date", e.target.value)}
                          className="flex-1 min-w-0 text-sm"
                          data-testid={`vacation-end-${v.name}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-shrink-0 px-2"
                          onClick={() => saveVacation(i)}
                          disabled={vacationSavingName === v.name}
                          data-testid={`vacation-save-${v.name}`}
                        >
                          {vacationSavingName === v.name ? "..." : "Salvesta"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Filtrid:</span>
                </div>
                
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-time-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_PERIODS.map((period) => (
                      <SelectItem key={period.value} value={period.value}>
                        {period.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedNurse} onValueChange={setSelectedNurse}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-nurse">
                    <SelectValue placeholder="All Nurses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Kõik õed</SelectItem>
                    {nurses.map((nurse) => (
                      <SelectItem key={nurse.nurse_id} value={nurse.nurse_id}>
                        {nurse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedWorkType} onValueChange={setSelectedWorkType}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-work-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Kõik töötüübid</SelectItem>
                    {WORK_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: type.color }}
                          />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Legend - Nurses with their colors */}
          <div className="flex flex-wrap gap-4">
            {nurses.map((nurse, index) => (
              <div key={nurse.nurse_id} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded" 
                  style={{ backgroundColor: NURSE_COLORS[index % NURSE_COLORS.length] }}
                />
                <span className="text-sm font-medium text-slate-700">{nurse.name}</span>
              </div>
            ))}
          </div>

          {/* Timeline Grid */}
          <Card className="border-slate-200" data-testid="schedule-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Graafiku ajatelg</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 spinner" />
                </div>
              ) : (
                <div className="overflow-x-auto timeline-scroll">
                  <div className="min-w-[600px]">
                    {/* Hours Header - 8:00 to 20:00 */}
                    <div className="flex border-b border-slate-200">
                      <div className="w-32 md:w-40 flex-shrink-0 p-3 bg-slate-50 border-r border-slate-200 text-xs font-medium text-slate-500">
                        Päev
                      </div>
                      {Array.from({ length: 13 }, (_, i) => (
                        <div 
                          key={i + 8}
                          className="timeline-header flex-1"
                        >
                          {(i + 8).toString().padStart(2, "0")}
                        </div>
                      ))}
                      <div className="w-20 flex-shrink-0 p-2 bg-slate-50 border-l border-slate-200 text-xs font-medium text-slate-500 text-center">
                        Tunnid
                      </div>
                    </div>

                    {/* Days Rows */}
                    {dateRange.days.map((day) => {
                      const dayStr = format(day, "yyyy-MM-dd");
                      const daySchedules = filteredSchedules.filter((s) => s.date === dayStr);
                      const dayHours = daySchedules.reduce((sum, s) => {
                        const start = parseTime(s.start_time ?? s.start_hour);
                        const end = parseTime(s.end_time ?? s.end_hour);
                        return sum + (end - start);
                      }, 0);

                      // Calculate rows for overlapping schedules - support both old and new format
                      const sortedSchedules = [...daySchedules].sort((a, b) => {
                        return parseTime(a.start_time ?? a.start_hour) - parseTime(b.start_time ?? b.start_hour);
                      });
                      const scheduleRows = [];
                      
                      sortedSchedules.forEach((schedule) => {
                        const schedStart = parseTime(schedule.start_time ?? schedule.start_hour);
                        const schedEnd = parseTime(schedule.end_time ?? schedule.end_hour);
                        let rowIndex = 0;
                        while (true) {
                          const rowSchedules = scheduleRows[rowIndex] || [];
                          const hasOverlap = rowSchedules.some((s) => {
                            const sStart = parseTime(s.start_time ?? s.start_hour);
                            const sEnd = parseTime(s.end_time ?? s.end_hour);
                            return !(schedEnd <= sStart || schedStart >= sEnd);
                          });
                          if (!hasOverlap) {
                            if (!scheduleRows[rowIndex]) scheduleRows[rowIndex] = [];
                            scheduleRows[rowIndex].push(schedule);
                            schedule._rowIndex = rowIndex;
                            break;
                          }
                          rowIndex++;
                        }
                      });

                      const totalRows = Math.max(scheduleRows.length, 1);
                      const rowHeight = totalRows > 1 ? Math.max(20, 64 / totalRows) : 64;
                      
                      // Timeline range: 8:00 to 20:00 (12 hours)
                      const TIMELINE_START = 8;
                      const TIMELINE_END = 20;
                      const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;

                      return (
                        <div key={dayStr} className="flex border-b border-slate-100 last:border-b-0">
                          <div className="w-32 md:w-40 flex-shrink-0 p-3 bg-white border-r border-slate-200 text-sm">
                            <div className="font-medium text-slate-900">
                              {format(day, "EEE")}
                            </div>
                            <div className="text-xs text-slate-500">
                              {format(day, "MMM d")}
                            </div>
                          </div>
                          
                          <div className="flex-1 flex relative" style={{ minHeight: `${Math.max(64, totalRows * rowHeight)}px` }}>
                            {Array.from({ length: 13 }, (_, i) => (
                              <div 
                                key={i + 8}
                                className="timeline-cell flex-1"
                                style={{ minHeight: `${Math.max(64, totalRows * rowHeight)}px` }}
                              />
                            ))}
                            
                            {/* Schedule Blocks */}
                            {daySchedules.map((schedule) => {
                              const workType = getWorkType(schedule.work_type);
                              const nurseColor = getNurseColor(schedule.nurse_id);
                              const startTime = parseTime(schedule.start_time ?? schedule.start_hour);
                              const endTime = parseTime(schedule.end_time ?? schedule.end_hour);
                              
                              // Clamp times to visible range (8-20)
                              const visibleStart = Math.max(startTime, TIMELINE_START);
                              const visibleEnd = Math.min(endTime, TIMELINE_END);
                              
                              // Skip if completely outside visible range
                              if (visibleStart >= TIMELINE_END || visibleEnd <= TIMELINE_START) {
                                return null;
                              }
                              
                              const leftPercent = ((visibleStart - TIMELINE_START) / TIMELINE_HOURS) * 100;
                              const widthPercent = ((visibleEnd - visibleStart) / TIMELINE_HOURS) * 100;
                              const topOffset = (schedule._rowIndex || 0) * rowHeight + 2;
                              
                              // For assistant_nurse: grey background with nurse color left border
                              const isAssistant = schedule.work_type === "assistant_nurse";
                              const bgColor = isAssistant ? "#6B7280" : nurseColor;

                              return (
                                <div
                                  key={schedule.schedule_id}
                                  className="absolute rounded-lg font-semibold flex items-center text-white shadow-md cursor-pointer hover:brightness-110 hover:shadow-lg transition-all group overflow-hidden"
                                  style={{
                                    left: `${leftPercent}%`,
                                    width: `${widthPercent}%`,
                                    top: `${topOffset}px`,
                                    height: `${rowHeight - 4}px`,
                                    backgroundColor: bgColor,
                                  }}
                                  title={`${getNurseName(schedule.nurse_id)} - ${workType.label} (${formatTime(startTime)} - ${formatTime(endTime)})`}
                                  data-testid={`schedule-block-${schedule.schedule_id}`}
                                >
                                  {/* Nurse color stripe on left for assistant work */}
                                  {isAssistant && (
                                    <div 
                                      className="w-2 h-full flex-shrink-0" 
                                      style={{ backgroundColor: nurseColor }}
                                    />
                                  )}
                                  <span className="truncate px-2 text-sm md:text-base font-bold drop-shadow-sm flex-1 text-center">
                                    {getNurseName(schedule.nurse_id)}
                                  </span>
                                  <div className="absolute top-0 right-0 hidden group-hover:flex">
                                    <button
                                      onClick={() => openEditDialog(schedule)}
                                      className="p-1 bg-white/20 hover:bg-white/40 rounded"
                                      data-testid={`edit-schedule-${schedule.schedule_id}`}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                                      className="p-1 bg-white/20 hover:bg-red-500 rounded"
                                      data-testid={`delete-schedule-${schedule.schedule_id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <div className="w-20 flex-shrink-0 p-2 bg-white border-l border-slate-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-slate-700">
                              {dayHours}h
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schedule List (Mobile friendly) */}
          <Card className="border-slate-200 md:hidden" data-testid="schedule-list-mobile">
            <CardHeader>
              <CardTitle className="text-lg">Graafiku nimekiri</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[300px]">
                {filteredSchedules.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    Selle perioodi graafikuid ei leitud
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredSchedules.map((schedule) => {
                      const workType = getWorkType(schedule.work_type);
                      const nurseColor = getNurseColor(schedule.nurse_id);
                      const startTime = parseTime(schedule.start_time ?? schedule.start_hour);
                      const endTime = parseTime(schedule.end_time ?? schedule.end_hour);
                      return (
                        <div key={schedule.schedule_id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-12 rounded-full" 
                              style={{ backgroundColor: nurseColor }}
                            />
                            <div>
                              <p className="font-bold text-lg text-slate-900">
                                {getNurseName(schedule.nurse_id)}
                              </p>
                              <p className="text-sm text-slate-500">
                                {format(parseISO(schedule.date), "EEE, MMM d")} • {formatTime(startTime)} - {formatTime(endTime)}
                              </p>
                              <Badge variant="outline" className={`badge-${schedule.work_type} mt-1`}>
                                {workType.label}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditDialog(schedule)}
                              className="p-2 hover:bg-slate-100 rounded-lg"
                            >
                              <Edit2 className="w-4 h-4 text-slate-500" />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                              className="p-2 hover:bg-red-100 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
